import { kanaToRomaji } from "./kanaToRomaji";

export type KanjiSearchItem = {
    symbol: string;
    meaning: string;
    jlptLevel?: number | null;
    readings?: string[];
};

export type KanjiSearchEntry<T extends KanjiSearchItem> = {
    item: T;
    haystack: string;
};

// Lowercases, strips Latin diacritics ("coracao" finds "coração") and folds
// full-width Latin from a Japanese IME ("ｗａｔｅｒ" finds "water") via NFKD. Kanji
// are untouched; kana would lose dakuten, but the haystack only holds meaning
// text, and kana in a query simply won't match anything there.
export function normalizeSearchText(text: string): string {
    return text
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .trim();
}

// Built once per list/language, so typing only runs cheap `includes` checks.
// The haystack covers meanings and readings, including romaji forms of readings.
export function buildKanjiSearchIndex<T extends KanjiSearchItem>(
    items: T[],
    meaningFor: (symbol: string, meaning: string) => string
): KanjiSearchEntry<T>[] {
    return items.map((item) => {
        const displayed = meaningFor(item.symbol, item.meaning) ?? "";
        const parts = displayed === item.meaning ? [displayed] : [displayed, item.meaning];
        const readings = item.readings ?? [];
        const romajiReadings = readings.map((reading) => kanaToRomaji(reading.replace(/\./g, "")));
        return { item, haystack: normalizeSearchText([...parts, ...readings, ...romajiReadings].join("\n")) };
    });
}

// A query matches a kanji when it contains that exact character (so a pasted
// run of kanji shows each one that exists) or when it is a substring of the
// meaning, reading, or romaji text.
export function searchKanjiIndex<T extends KanjiSearchItem>(index: KanjiSearchEntry<T>[], query: string): T[] {
    const trimmed = query.trim();
    if (!trimmed) return index.map((entry) => entry.item);
    const typedCharacters = new Set(Array.from(trimmed));
    const needle = normalizeSearchText(trimmed);
    return index
        .filter((entry) => typedCharacters.has(entry.item.symbol) || entry.haystack.includes(needle))
        .map((entry) => entry.item);
}
